import { useEffect, useRef } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useHistory } from 'react-router-dom';

import type { RootState } from '../../../services/serviceTypes';
import ChatFlowControllerV1 from '../controllers/chatFlowController';
import ChatFlowController from '../controllers/chatFlowController.v2';
import {
  useInitiateOnboardMutation,
  useLazyGenerateCategoryTemplateQuery,
  useSaveUseCaseConfigurationMutation,
  useTestConnectionMutation,
  useUpdateApplicationMutation,
} from '../data-store/copilotApi';
import type { ParsedTemplateData } from '../utils/templateParsers';

// import useFormSubmissionHandler from './useFormSubmissionHandler';

const USE_V2_CONTROLLER = true;

const ChatFlowControllerClass = USE_V2_CONTROLLER
  ? ChatFlowController
  : ChatFlowControllerV1;

export const useChatFlowController = () => {
  const dispatch = useDispatch();
  const store = useStore() as { getState: () => unknown };
  const history = useHistory();
  const controllerRef = useRef<InstanceType<
    typeof ChatFlowControllerClass
  > | null>(null);

  const [initiateOnboard] = useInitiateOnboardMutation();
  const [testConnection] = useTestConnectionMutation();
  const [saveUseCaseConfiguration] = useSaveUseCaseConfigurationMutation();
  const [updateApplication] = useUpdateApplicationMutation();
  const [generateCategoryTemplate] = useLazyGenerateCategoryTemplateQuery();

  // const { handleFormSubmit: handleFormSubmitHook } = useFormSubmissionHandler(
  //   store.getState
  // );

  const { nameToLabelMap } = useSelector(
    (state: RootState) => state.copilot.navigation
  );
  const { parsedTemplates } = useSelector((state: RootState) => state.copilot);

  useEffect(() => {
    if (!controllerRef.current) {
      controllerRef.current = new ChatFlowControllerClass({
        dispatch,
        getState: store.getState,
        history,
        services: {
          initiateOnboard: initiateOnboard as (
            payload: unknown
          ) => Promise<unknown>,
          testConnection: testConnection as (
            payload: unknown
          ) => Promise<unknown>,
          saveUseCaseConfiguration: saveUseCaseConfiguration as (
            payload: unknown
          ) => Promise<unknown>,
          updateApplication: updateApplication as (
            payload: unknown
          ) => Promise<unknown>,
          generateCategoryTemplate: generateCategoryTemplate as (
            payload: unknown
          ) => Promise<unknown>,
        },
      });
    }
  }, [
    dispatch,
    store,
    history,
    initiateOnboard,
    testConnection,
    saveUseCaseConfiguration,
    updateApplication,
    generateCategoryTemplate,
  ]);

  useEffect(() => {
    if (
      controllerRef.current &&
      Object.keys(nameToLabelMap).length > 0 &&
      Object.keys(parsedTemplates).length > 0
    ) {
      controllerRef.current.initialize(
        nameToLabelMap,
        parsedTemplates as ParsedTemplateData
      );
    }
  }, [nameToLabelMap, parsedTemplates]);

  return {
    controller: controllerRef.current,
    handleFormSubmit: (
      stepName: string,
      formData: Record<string, unknown>,
      isValid: boolean
    ) => {
      const controller = controllerRef.current;

      if (controller && typeof controller.handleFormSubmit === 'function') {
        Promise.resolve(
          controller.handleFormSubmit(stepName, formData, isValid)
        ).catch(() => {
          // Handle errors silently
        });
      }
    },
    handleFormSkip: (stepName?: string, entitlementIndex?: number) => {
      if (controllerRef.current) {
        switch (stepName) {
          case 'AddAccess':
            controllerRef.current.skipAddAccess();
            break;
          case 'RemoveAccess':
            controllerRef.current.skipFromRemoveAccess(entitlementIndex);
            break;
          default:
            controllerRef.current.skipToFinalConfirmation();
            break;
        }
      }
    },
    handleSkip: (
      currentStep: string,
      nextStep: string,
      entitlementIndex?: number
    ) => {
      if (controllerRef.current) {
        controllerRef.current.handleSkip(
          currentStep,
          nextStep,
          entitlementIndex ?? 0
        );
      }
    },
  };
};

export default useChatFlowController;
